'use client';

import React, { useState } from 'react';
import Container from '@/components/ui/Container';
import { Search, Tag, Trash } from 'lucide-react';
import { Dictionary } from '@/app/[lang]/dictonaries';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { TagEntry } from '@/utils/contentful/types';

type ControlPanelProps = {
  setSearchQuery: (query: string) => void;
  searchQuery: string;
  setSelectedTags: React.Dispatch<React.SetStateAction<TagEntry[]>>;
  selectedTags: TagEntry[];
  options?: {
    showSearch?: boolean;
    showTagSearch?: boolean;
  };
  tags: TagEntry[];
  dict: Dictionary;
};

export default function ControlPanel({
  setSearchQuery,
  searchQuery,
  setSelectedTags,
  selectedTags,
  options,
  tags,
  dict
}: ControlPanelProps) {
  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchQuery(e.target.value);
  }

  function toggleOption(id: string) {
    setSelectedTags((prev: TagEntry[]) => {
      const isSelected = prev.find((tag) => tag.sys.id === id);

      if (isSelected) {
        return prev.filter((tag) => tag.sys.id !== id);
      } else {
        const newTag = tags.find((tag) => tag.sys.id === id);

        if (newTag) {
          return [...prev, newTag];
        }
      }
      return prev;
    });
  }

  return (
    <div>
      <Container>
        <div className="flex flex-col gap-2 sm:flex-row sm:h-full">
          {options?.showSearch && (
            <div className="inline-flex items-center gap-2 text-zinc-800 border-2 border-gray-300 px-3 py-2 rounded-2xl">
              <Search className="text-gray-400" />
              <input
                type="text"
                placeholder={dict.pages.blog.controls.search.placeholder}
                className="outline-none"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          )}
          {options?.showTagSearch && (
            <div>
              <Popover>
                <PopoverTrigger asChild>
                  <div className="flex items-center gap-2">
                    <div className="w-full flex flex-wrap items-center gap-2 text-gray-400 border-2 border-gray-300 px-3 py-2 rounded-2xl cursor-pointer">
                      <Tag />
                      {selectedTags.length > 0 ? (
                        selectedTags.slice(0, 2).map((t, index) => {
                          return (
                            <span
                              key={t.sys.id}
                              className="bg-gray-200 px-2 py-1 rounded text-gray-800"
                            >
                              {t.fields.tagName}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-gray-400">
                          {dict.pages.blog.controls.tags.title}
                        </span>
                      )}

                      {selectedTags.length > 2 && (
                        <span className="text-gray-500">
                          +{selectedTags.length - 2}{' '}
                          {dict.pages.blog.tag_count.extra}
                        </span>
                      )}
                    </div>
                    <Trash
                      className="text-gray-400 w-7 h-7 cursor-pointer"
                      onClick={() => setSelectedTags([])}
                    />
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 z-20">
                  <Command>
                    <CommandInput
                      placeholder={
                        dict.pages.blog.controls.tags.tag_search_placeholder
                      }
                    />
                    <CommandList>
                      {tags.map((tag) => (
                        <CommandItem
                          key={tag.sys.id}
                          onSelect={() => toggleOption(tag.sys.id)}
                          className="cursor-pointer hover:bg-gray-100"
                        >
                          <Checkbox
                            checked={selectedTags.some(
                              (t) => t.sys.id === tag.sys.id
                            )}
                            onCheckedChange={() => toggleOption(tag.sys.id)}
                            className="mr-2"
                          />
                          {tag.fields.tagName}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}
